import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import SEO from "../components/SEO";
import { FaCheckCircle, FaCog, FaTachometerAlt, FaTools } from "react-icons/fa";
import "./Programming.css";
import heroImage from "../assets/pc.webp";
import Footer from "../components/Footer";

const Programming = () => {
  const [selectedBrands, setSelectedBrands] = useState([]);
  const navigate = useNavigate();

  // Brand data grouped by categories
  const brands = [
    // VAG Group
    { id: 1, name: "Volkswagen", description: "Wide range of passenger cars and SUVs like Golf, Tiguan, and ID.4 electric vehicles", category: "VAG Group" },
    { id: 2, name: "Audi", description: "Premium luxury brand known for sophisticated design and advanced technology", category: "VAG Group" },
    { id: 3, name: "Škoda", description: "Czech brand known for practical, value-for-money, and spacious family cars", category: "VAG Group" },
    { id: 4, name: "SEAT", description: "Spanish brand offering stylish and affordable compact cars", category: "VAG Group" },
    { id: 5, name: "Cupra", description: "Standalone sporty brand offering unique models like the Formentor", category: "VAG Group" },
    { id: 6, name: "Porsche", description: "Iconic German manufacturer of high-performance sports cars and luxury SUVs", category: "VAG Group" },

    // Other Premium Brands
    { id: 9, name: "Mercedes-Benz", description: "German luxury automotive brand known for innovation and prestige", category: "Premium Brands" },
    { id: 10, name: "BMW", description: "Bavarian luxury brand focused on performance and driving dynamics", category: "Premium Brands" },

    // Mass Market Brands
    { id: 11, name: "Ford", description: "American automotive manufacturer with global presence", category: "Mass Market" },
    { id: 12, name: "Toyota", description: "Japanese brand renowned for reliability and hybrid technology", category: "Mass Market" },
    { id: 13, name: "Honda", description: "Japanese manufacturer known for engineering excellence and fuel efficiency", category: "Mass Market" },
  ];

  // Handle checkbox toggle
  const handleToggle = (brand) => {
    const isSelected = selectedBrands.find((b) => b.id === brand.id);

    if (isSelected) {
      setSelectedBrands(selectedBrands.filter((b) => b.id !== brand.id));
    } else {
      setSelectedBrands([...selectedBrands, brand]);
    }
  };

  // Handle request service
  const handleRequestService = () => {
    if (selectedBrands.length === 0) return;

    const brandNames = selectedBrands.map((brand) => brand.name).join(", ");
    const subject = `Programming & Coding Service Request - ${brandNames}`;
    const message = `Hello,\n\nI am interested in programming and coding services for the following vehicle brands:\n\n${selectedBrands
      .map((brand) => `• ${brand.name} (${brand.category})`)
      .join(
        "\n"
      )}\n\nWhat I'm looking for in these selected brands:\n- Software updates and programming\n- Module coding and configuration\n- Diagnostic programming services\n- Custom feature activation\n\nPlease provide me with detailed information about your services and availability.\n\nThank you!`;

    navigate("/contact", {
      state: {
        subject: subject,
        message: message,
      },
    });
  };

  const canonical = "https://carforensic.uk/programming";

  const programmingSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Vehicle Programming and Coding",
    "url": canonical,
    "areaServed": ["Kent", "Maidstone", "Gillingham", "Sevenoaks", "Sittingbourne"],
    "provider": {
      "@type": "AutoRepair",
      "name": "Car Forensics LTD",
      "url": "https://carforensic.uk/",
      "telephone": "+447501911970",
      "email": "carforensicsltd@gmail.com",
    },
    "description":
      "Professional vehicle programming and coding services across Kent including Maidstone, Gillingham, Sevenoaks and Sittingbourne."
  };

  return (
    <>
      <SEO
        title="Car Forensics LTD | Programming & Coding"
        description="Professional vehicle programming, coding and software services across Kent — including Maidstone, Gillingham, Sevenoaks and Sittingbourne. Select your vehicle brand and request support from Car Forensics LTD."
        canonical={canonical}
      />

      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(programmingSchema)}
        </script>
      </Helmet>

      <div className="programming-page">
        {/* Hero Section */}
        <section
          className="programming-hero"
          style={{ backgroundImage: `url(${heroImage})` }}
        >
          <div className="heros-overlay"></div>
          <div className="programming-hero-content">
            <div className="hero-icon-wrapper">
              <FaCog className="hero-icon rotating" />
            </div>
            <h1>Programming & Coding Services</h1>
            <p>Professional vehicle software programming and coding solutions</p>
            <div className="hero-stats">
              <div className="stat-item">
                <FaTachometerAlt />
                <span>Multi-Brand Expertise</span>
              </div>
              <div className="stat-item">
                <FaTools />
                <span>Latest Software</span>
              </div>
              <div className="stat-item">
                <FaCheckCircle />
                <span>Guaranteed Quality</span>
              </div>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <section className="programming-services-section">
          <div className="programming-container">
            <div className="section-intro">
              <h2>Select Your Vehicle Brands</h2>
              <p>
                Choose from our comprehensive range of supported vehicle brands
                for programming and coding services.
              </p>
            </div>

            <div className="programming-content-wrapper">
              {/* Brands Grid */}
              <div className="brands-selection-area">
                {/* VAG Brands Section */}
                <div className="brand-category-section">
                  <h3 className="category-heading">VAG Brands</h3>
                  <div className="brands-grid">
                    {brands
                      .filter((brand) => brand.category === "VAG Group")
                      .map((brand) => {
                        const isSelected = selectedBrands.find(
                          (b) => b.id === brand.id
                        );
                        return (
                          <label
                            key={brand.id}
                            className={`brand-checkbox-card ${
                              isSelected ? "selected" : ""
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={!!isSelected}
                              onChange={() => handleToggle(brand)}
                            />
                            <div className="checkbox-custom">
                              <FaCheckCircle className="check-icon" />
                            </div>
                            <div className="brand-info">
                              <div className="brand-details-wrapper">
                                <span className="brand-name">{brand.name}</span>
                                <span className="brand-category-tag">
                                  {brand.category}
                                </span>
                              </div>
                              <p className="brand-description">
                                {brand.description}
                              </p>
                            </div>
                          </label>
                        );
                      })}
                  </div>
                </div>

                {/* Other Brands Section */}
                <div className="brand-category-section">
                  <h3 className="category-heading">Other Brands</h3>
                  <div className="brands-grid">
                    {brands
                      .filter((brand) => brand.category !== "VAG Group")
                      .map((brand) => {
                        const isSelected = selectedBrands.find(
                          (b) => b.id === brand.id
                        );
                        return (
                          <label
                            key={brand.id}
                            className={`brand-checkbox-card ${
                              isSelected ? "selected" : ""
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={!!isSelected}
                              onChange={() => handleToggle(brand)}
                            />
                            <div className="checkbox-custom">
                              <FaCheckCircle className="check-icon" />
                            </div>
                            <div className="brand-info">
                              <div className="brand-details-wrapper">
                                <span className="brand-name">{brand.name}</span>
                                <span className="brand-category-tag">
                                  {brand.category}
                                </span>
                              </div>
                              <p className="brand-description">
                                {brand.description}
                              </p>
                            </div>
                          </label>
                        );
                      })}
                  </div>
                </div>
              </div>

              {/* Summary Sidebar */}
              <div className="summary-sidebar">
                <div className="summary-card">
                  <h3 className="summary-title">
                    <FaCheckCircle className="summary-icon" />
                    Selected Brands
                  </h3>

                  {selectedBrands.length === 0 ? (
                    <div className="empty-state">
                      <p>No brands selected yet</p>
                      <span>
                        Select brands from the left to see your selection
                      </span>
                    </div>
                  ) : (
                    <>
                      <div className="selected-brands-list">
                        {selectedBrands.map((brand) => (
                          <div key={brand.id} className="selected-brand-item">
                            <div className="brand-detail">
                              <span className="item-name">{brand.name}</span>
                              <span className="item-category">
                                {brand.category}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>

                      <div className="summary-divider"></div>

                      <div className="brand-count-section">
                        <div className="count-row">
                          <span>Total Selected Brands</span>
                          <span className="count-number">
                            {selectedBrands.length}
                          </span>
                        </div>
                      </div>

                      <button
                        className="cta-button-programming"
                        onClick={handleRequestService}
                      >
                        <span>Request Service</span>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <line x1="5" y1="12" x2="19" y2="12"></line>
                          <polyline points="12 5 19 12 12 19"></polyline>
                        </svg>
                      </button>

                      <div className="info-note">
                        <p>
                          Programming and coding services available for selected
                          brands. Contact us for specific requirements.
                        </p>
                      </div>
                    </>
                  )}
                </div>

                {/* Contact Card */}
                <div className="contact-quick-card">
                  <h4>Need Assistance?</h4>
                  <p>Our experts are here to help</p>
                  <a
                    href="tel:+447501911970"
                    className="phone-link-programming"
                  >
                    📞 +447501911970
                  </a>

                  {/* internal link fixed (no full reload) */}
                  <Link to="/contact" className="contact-link-programming">
                    Contact Us
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Info Section */}
        <section className="programming-info-section">
          <div className="programming-container">
            <div className="infoss-grid">
              <div className="infoss-card">
                <div className="infoss-icon">
                  <FaCheckCircle />
                </div>
                <h3>Multi-Brand Support</h3>
                <p>
                  Comprehensive programming and coding services for all major
                  automotive brands and models.
                </p>
              </div>
              <div className="infoss-card">
                <div className="infoss-icon">
                  <FaTachometerAlt />
                </div>
                <h3>Advanced Solutions</h3>
                <p>
                  From basic coding to complex programming tasks using the latest
                  diagnostic equipment.
                </p>
              </div>
              <div className="infoss-card">
                <div className="infoss-icon">
                  <FaTools />
                </div>
                <h3>Expert Technicians</h3>
                <p>
                  Certified professionals with extensive experience in automotive
                  software and programming.
                </p>
              </div>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default Programming;
